import { useState, useEffect, useMemo } from 'react'
import * as Yup from 'yup'
import { useFormik } from 'formik'
import { useTranslation, Trans } from 'react-i18next'
import { endOfDay, format } from 'date-fns'

import {
  Modal,
  ActionModal,
  Text,
  Spacer,
  FormField,
  AcknowledgeModal,
  Select,
  DateInput,
  CurrencyInput,
  Typography,
  SegmentedControl
} from '@percent/lemonade'
import type { SelectOption } from '@percent/lemonade'
import { useServices } from '@percent/partner-dashboard/context/serviceContext/ServiceContext'
import { useMutation, useQuery } from '@percent/partner-dashboard/common/hooks'
import { formatMoney } from '@percent/utility'
import type { EmployeesModalProps } from './EmployeesModal.types'
import type { FundingSource } from '@percent/partner-dashboard/services/walletBalance/walletBalanceService.types'
import type { AddWalletBalanceProps } from '@percent/partner-dashboard/services/workplaceGiving/workplaceGivingService.types'
import styles from './AddWalletBalanceModal.module.scss'

type SummaryRowProps = Readonly<{
  title: string
  value: string
  testId: string
}>

function SummaryRow({ title, value, testId }: SummaryRowProps) {
  return (
    <div className={styles.summaryRow}>
      <div className={styles.summaryRowTitle} data-testid={`${testId}-title`}>
        {title}
      </div>
      <div className={styles.summaryRowBody} data-testid={`${testId}-body`}>
        {value}
      </div>
    </div>
  )
}

type AdjustmentDirection = 'add' | 'remove'

const ADJUSTMENT_COPY = {
  add: {
    confirmTitle: 'dialog.employees.wallet.confirm.title',
    confirmDescriptionWithPossessive: 'dialog.employees.wallet.confirm.descriptionWithPossessive',
    confirmDescriptionWithoutPossessive: 'dialog.employees.wallet.confirm.descriptionWithoutPossessive',
    confirmAmount: 'dialog.employees.wallet.confirm.adding',
    confirmButton: 'button.confirmTopUp',
    confirmButtonTestId: 'confirm-wallet-top-up',
    successTitle: 'dialog.employees.wallet.success.title',
    successDescriptionWithPossessive: 'dialog.employees.wallet.success.descriptionWithPossessive',
    successDescriptionWithoutPossessive: 'dialog.employees.wallet.success.descriptionWithoutPossessive'
  },
  remove: {
    confirmTitle: 'dialog.employees.wallet.confirm.removeTitle',
    confirmDescriptionWithPossessive: 'dialog.employees.wallet.confirm.removeDescriptionWithPossessive',
    confirmDescriptionWithoutPossessive: 'dialog.employees.wallet.confirm.removeDescriptionWithoutPossessive',
    confirmAmount: 'dialog.employees.wallet.confirm.removing',
    confirmButton: 'button.confirmRemoval',
    confirmButtonTestId: 'confirm-wallet-removal',
    successTitle: 'dialog.employees.wallet.success.removeTitle',
    successDescriptionWithPossessive: 'dialog.employees.wallet.success.removeDescriptionWithPossessive',
    successDescriptionWithoutPossessive: 'dialog.employees.wallet.success.removeDescriptionWithoutPossessive'
  }
} as const

export function AddWalletBalanceModal({ isOpen, onClose, employee, refresh }: Readonly<EmployeesModalProps>) {
  const [step, setStep] = useState<'form' | 'confirm'>('form')
  const [isSuccess, setIsSuccess] = useState<boolean>(false)
  const [isError, setIsError] = useState<boolean>(false)
  const [fundingSourceOptions, setFundingSourceOptions] = useState<SelectOption[]>([])
  const {
    t,
    i18n: { language: locale }
  } = useTranslation()
  const { walletBalanceService } = useServices()
  // Recompute "today" each time the modal opens so a long-mounted parent doesn't freeze the floor across midnight.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const minDate = useMemo(() => new Date(), [isOpen])

  // The API takes a positive amount on separate add and remove endpoints, and only a top-up can expire.
  const adjustWalletBalance = ({ amount, expiresAt, ...adjustment }: AddWalletBalanceProps) =>
    amount.amount < 0
      ? walletBalanceService.removeWalletBalance({ ...adjustment, amount: { ...amount, amount: -amount.amount } })
      : walletBalanceService.addWalletBalance({ ...adjustment, amount, expiresAt })

  const [{ isLoading, data }, { apiFunc }] = useMutation(
    adjustWalletBalance,
    () => {
      onClose()
      setStep('form')
      setIsSuccess(true)
      refresh()
    },
    () => {
      onClose()
      setStep('form')
      setIsError(true)
    }
  )

  const [{ body: fundingSourcesData, isLoading: isFundingSourcesLoading }] = useQuery(
    walletBalanceService.getFundingSources,
    {},
    isOpen
  )

  const showFundingSourceSelect = !isFundingSourcesLoading && fundingSourceOptions.length > 0
  const currentBalance = employee.wallet?.balance ?? { amount: 0, currency: employee.currency }

  useEffect(() => {
    if (fundingSourcesData?.data) {
      const options: SelectOption[] = fundingSourcesData.data
        .filter((source: FundingSource) => !source.disabledAt)
        .map((source: FundingSource) => ({
          label: source.name,
          value: source.id
        }))
      setFundingSourceOptions(options)
    }
  }, [fundingSourcesData])

  const handleCloseSuccessModal = () => {
    setIsSuccess(false)
    resetForm()
  }

  const handleCloseErrorModal = () => {
    setIsError(false)
    resetForm()
  }

  const { errors, touched, dirty, values, handleSubmit, isValid, setFieldValue, resetForm } = useFormik({
    initialValues: {
      direction: 'add' as AdjustmentDirection,
      amount: 0,
      fundingSourceId: '',
      expiresAt: null as Date | null
    },
    validationSchema: Yup.object().shape({
      amount: Yup.number()
        .required(t('errorMessage.required'))
        .test('non-zero', t('errorMessage.nonZeroNumber'), amount => amount !== 0)
        .when('direction', {
          is: 'remove',
          then: schema => schema.max(currentBalance.amount, t('errorMessage.walletRemovalExceedsBalance'))
        }),
      fundingSourceId: Yup.string().optional(),
      expiresAt: Yup.date().nullable().optional()
    }),
    onSubmit: () => {
      setStep('confirm')
    }
  })

  const isRemoval = values.direction === 'remove'
  const signedAmount = isRemoval ? -values.amount : values.amount
  const adjustmentCopy = ADJUSTMENT_COPY[isRemoval ? 'remove' : 'add']
  const formattedCurrentBalance = formatMoney(currentBalance, { locale, hideDecimalsIfZero: true })
  const formattedAmount = formatMoney(
    { amount: values.amount, currency: employee.currency },
    { locale, hideDecimalsIfZero: true }
  )
  const formattedNewBalance = formatMoney(
    { amount: signedAmount + currentBalance.amount, currency: employee.currency },
    { locale, hideDecimalsIfZero: true }
  )
  const selectedFundingSourceName = fundingSourceOptions.find(option => option.value === values.fundingSourceId)?.label

  const handleConfirm = () => {
    apiFunc({
      accountId: employee.id,
      amount: {
        amount: signedAmount,
        currency: employee.currency
      },
      ...(values.fundingSourceId && { fundingSourceId: values.fundingSourceId }),
      // End-of-day in the partner's local timezone (intentional — top-up should
      // expire at the close of the picked calendar day for the partner, not in UTC).
      ...(values.expiresAt && { expiresAt: endOfDay(values.expiresAt).toISOString() })
    })
  }

  const handleDirectionChange = (direction: string) => {
    resetForm({
      values: {
        direction: direction as AdjustmentDirection,
        amount: 0,
        fundingSourceId: '',
        expiresAt: null
      }
    })
  }

  const amountDescription = () => {
    if (values.amount) {
      return t('dialog.employees.wallet.description', { balance: formattedNewBalance })
    }

    return isRemoval ? t('dialog.employees.wallet.removeLimitDescription', { balance: formattedCurrentBalance }) : ''
  }

  const handleBack = () => {
    setStep('form')
  }

  const handleClose = () => {
    resetForm()
    setStep('form')
    onClose()
  }

  return (
    <>
      <Modal
        open={isOpen}
        onClose={step === 'confirm' ? handleBack : handleClose}
        aria-labelledby={step === 'confirm' ? 'add-wallet-balance-confirm' : 'add-wallet-balance-modal'}
      >
        {step === 'form' ? (
          <ActionModal
            title={t('dialog.employees.wallet.adjustTitle')}
            primaryButtonText={t('button.continue')}
            secondaryButtonText={t('button.cancel')}
            variant={isValid && dirty ? 'primary' : 'secondary'}
            disabled={!(isValid && dirty)}
            handleClose={handleClose}
            handleSubmit={handleSubmit}
            primaryBtnTestId="reject-request"
            secondaryBtnTestId="cancel-request"
          >
            <Text size="small">
              {employee.fullName
                ? t('dialog.employees.wallet.subTitleWithPossessive', {
                    name: employee.fullName,
                    email: employee.email,
                    balance: formattedCurrentBalance
                  })
                : t('dialog.employees.wallet.subTitleWithoutPossessive', {
                    email: employee.email,
                    balance: formattedCurrentBalance
                  })}
            </Text>
            <form onSubmit={handleSubmit}>
              <Spacer size={4} axis="vertical" />
              <SegmentedControl
                fullWidth
                value={values.direction}
                onChange={handleDirectionChange}
                data={[
                  { label: t('dialog.employees.wallet.direction.add'), value: 'add' },
                  { label: t('dialog.employees.wallet.direction.remove'), value: 'remove' }
                ]}
              />
              <Spacer size={4} axis="vertical" />
              <FormField
                label={t(isRemoval ? 'dialog.employees.wallet.removeLabel' : 'dialog.employees.wallet.addLabel')}
                necessity="required"
                status={errors.amount ? 'danger' : 'default'}
                statusMessage={errors.amount}
                description={amountDescription()}
                data-testid="amountInput"
              >
                <CurrencyInput
                  key={values.direction}
                  id="amount"
                  name="amount"
                  status={touched.amount && errors.amount ? 'danger' : 'default'}
                  value={values?.amount ? { amount: values.amount, currency: employee.currency } : undefined}
                  onValueChange={value => setFieldValue('amount', value?.amount)}
                  currency={employee.currency}
                  locale={locale}
                  allowNegativeValue={false}
                  placeholder={{ amount: 1000, currency: employee.currency }}
                />
              </FormField>
              <Spacer size={4} axis="vertical" />
              {!isRemoval && showFundingSourceSelect && (
                <>
                  <FormField
                    label={t('dialog.employees.wallet.fundingSource.label')}
                    necessity="optional"
                    status={errors.fundingSourceId ? 'danger' : 'default'}
                    statusMessage={errors.fundingSourceId}
                    data-testid="fundingSourceSelect"
                  >
                    <Select
                      placeholder={t('dialog.employees.wallet.fundingSource.placeholder')}
                      disabled={isFundingSourcesLoading}
                      options={fundingSourceOptions}
                      onChange={event => setFieldValue('fundingSourceId', event.value)}
                      defaultValue={fundingSourceOptions.find(option => option.value === values.fundingSourceId)}
                    />
                  </FormField>
                  <Spacer size={4} axis="vertical" />
                </>
              )}
              {!isRemoval && (
                <>
                  <FormField
                    label={t('dialog.employees.wallet.expiresAt.label')}
                    necessity="optional"
                    description={t('dialog.employees.wallet.expiresAt.description')}
                  >
                    <DateInput
                      name="expiresAt"
                      data-testid="expiresAt"
                      value={values.expiresAt}
                      onChange={date => setFieldValue('expiresAt', date)}
                      minDate={minDate}
                      clearable
                      ariaLabels={{
                        nextMonth: t('dialog.employees.wallet.expiresAt.aria.nextMonth'),
                        previousMonth: t('dialog.employees.wallet.expiresAt.aria.previousMonth')
                      }}
                    />
                  </FormField>
                  <Spacer size={4} axis="vertical" />
                </>
              )}
            </form>
          </ActionModal>
        ) : (
          <ActionModal
            title={t(adjustmentCopy.confirmTitle)}
            primaryButtonText={t(adjustmentCopy.confirmButton)}
            secondaryButtonText={t('button.back')}
            disabled={isLoading}
            loading={isLoading}
            handleClose={handleBack}
            handleSubmit={handleConfirm}
            primaryBtnTestId={adjustmentCopy.confirmButtonTestId}
            secondaryBtnTestId="back-to-wallet-form"
          >
            <Typography>
              {employee.fullName ? (
                <Trans
                  i18nKey={adjustmentCopy.confirmDescriptionWithPossessive}
                  values={{ amount: formattedAmount, name: employee.fullName }}
                  components={{ b: <strong /> }}
                />
              ) : (
                <Trans
                  i18nKey={adjustmentCopy.confirmDescriptionWithoutPossessive}
                  values={{ amount: formattedAmount, email: employee.email }}
                  components={{ b: <strong /> }}
                />
              )}
            </Typography>
            <Spacer size={4} axis="vertical" />
            <div data-testid="add-wallet-balance-summary">
              <SummaryRow
                title={t('dialog.employees.wallet.confirm.currentBalance')}
                testId="confirm-current-balance"
                value={formattedCurrentBalance}
              />
              <SummaryRow title={t(adjustmentCopy.confirmAmount)} testId="confirm-amount" value={formattedAmount} />
              {selectedFundingSourceName && (
                <SummaryRow
                  title={t('dialog.employees.wallet.confirm.fundingSource')}
                  testId="confirm-funding-source"
                  value={selectedFundingSourceName}
                />
              )}
              {!isRemoval && (
                <SummaryRow
                  title={t('dialog.employees.wallet.confirm.expires')}
                  testId="confirm-expires"
                  value={
                    values.expiresAt
                      ? format(values.expiresAt, 'MMM d, yyyy')
                      : t('typography.employeeDetails.wallet.noExpiration')
                  }
                />
              )}
              <SummaryRow
                title={t('dialog.employees.wallet.confirm.newBalance')}
                testId="confirm-new-balance"
                value={formattedNewBalance}
              />
            </div>
          </ActionModal>
        )}
      </Modal>

      {data && (
        <Modal open={isSuccess} onClose={handleCloseSuccessModal} aria-labelledby="add-wallet-balance-success">
          <AcknowledgeModal
            result="positive"
            title={t(adjustmentCopy.successTitle)}
            description={
              employee.fullName ? (
                <Trans
                  i18nKey={adjustmentCopy.successDescriptionWithPossessive}
                  values={{
                    amount: formattedAmount,
                    name: employee.fullName,
                    balance: formatMoney(data.data.data.balance, { locale, hideDecimalsIfZero: true })
                  }}
                  components={{ b: <strong /> }}
                />
              ) : (
                <Trans
                  i18nKey={adjustmentCopy.successDescriptionWithoutPossessive}
                  values={{
                    amount: formattedAmount,
                    email: employee.email,
                    balance: formatMoney(data.data.data.balance, { locale, hideDecimalsIfZero: true })
                  }}
                  components={{ b: <strong /> }}
                />
              )
            }
            buttonText={t('button.close')}
            handleClose={handleCloseSuccessModal}
            buttonTestId="success-modal-btn"
          />
        </Modal>
      )}

      <Modal open={isError} onClose={handleCloseErrorModal} aria-labelledby="add-wallet-balance-error">
        <AcknowledgeModal
          result="negative"
          title={t('dialog.employees.wallet.error.title')}
          description={t('dialog.error.description')}
          buttonText={t('button.close')}
          handleClose={handleCloseErrorModal}
          buttonTestId="error-modal-btn"
        />
      </Modal>
    </>
  )
}
